import React, { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { StyleSheet, ViewProps } from 'react-native'
import { object, string } from 'yup'
import { Draft, WorkspaceTypeData, WorkspaceTypeSpecification, useWorkspaceType } from 'zens-store'
import { RadioGroup } from 'zens-storybook'
import { Form, FormProps, NavigationConfirm } from '@dms/component'

import HoldTimeSelect from './holdtimeselect'
import Specifications from './specifications'

const styles = StyleSheet.create({
  specifications: {
    flex: 1,
    maxWidth: '100%',
  },
})

interface Props extends Omit<FormProps, 'draft' | 'config' | 'schema' | 'buttons'> {
  companyId: number
  workspaceTypeId?: number
  onSave?: (draft: Draft<WorkspaceTypeData>) => Promise<void>
  onDelete?: (draft: Draft<WorkspaceTypeData>) => Promise<void>
}
const WorkspaceTypeForm = (props: Props) => {
  const { companyId, workspaceTypeId = 0, onSave, onDelete, ...otherProps } = props
  const { t } = useTranslation()
  const workspaceType = useWorkspaceType({ workspaceTypeId })
  const draft = useMemo(
    () =>
      new Draft<WorkspaceTypeData>({
        id: 0,
        holdtime: 0,
        ...workspaceType.data?.data,
      }),
    [workspaceType.data],
  )
  const getSpecifications = () => {
    const specifications: WorkspaceTypeSpecification[] = []
    if (draft.data.multi_person) specifications.push(WorkspaceTypeSpecification.CoWorking)
    if (draft.data.sound_proof) specifications.push(WorkspaceTypeSpecification.SoundProof)
    return specifications
  }

  const updateSpecifications = (specifications: WorkspaceTypeSpecification[]) => {
    draft.update('multi_person', specifications.indexOf(WorkspaceTypeSpecification.CoWorking) >= 0)
    draft.update('sound_proof', specifications.indexOf(WorkspaceTypeSpecification.SoundProof) >= 0)
  }

  const config = {
    groups: [
      {
        label: t('General information'),
        fields: [
          {
            key: 'name',
            label: t('Workspace type name'),
          },
        ],
      },
      {
        label: t('Properties'),
        fields: [
          {
            key: 'specifications',
            FieldInputComponent: Specifications,
            fieldProps: {
              value: getSpecifications(),
              onChange: updateSpecifications,
              style: styles.specifications,
            },
          },
        ],
      },
      {
        label: t('Settings'),
        fields: [
          {
            key: 'holdtime',
            label: t('Hold time'),
            help: t(
              'Setting a hold time for a workspace type ensures the workspace to hold an occupied status for a set time.',
            ),
            FieldInputComponent: HoldTimeSelect,
          },
        ],
      },
    ],
  }
  const schema = object({
    name: string().required(t('Workspace type name is required')),
  })

  const buttons = [
    [
      {
        title: t('Save'),
        onPress: onSave,
        validate: true,
      },
      {
        title: t('Cancel'),
        href: { pathname: `/workspacetypes`, params: { companyId } },
        light: true,
      },
    ],
    ...(onDelete
      ? [
          {
            title: t('Delete workspace type'),
            onPress: onDelete,
            left: 'trash' as const,
            danger: true,
          },
        ]
      : []),
  ]
  return (
    <>
      <Form draft={draft} config={config} schema={schema} buttons={buttons} {...otherProps} />
      <NavigationConfirm draft={draft} />
    </>
  )
}

interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  const { t } = useTranslation()
  const config = {
    groups: [
      {
        label: t('General information'),
        fields: [
          {
            key: 'name',
            label: t('Workspace type name'),
          },
        ],
      },
      {
        label: t('Properties'),
        fields: [
          {
            key: 'specifications',
            FieldInputComponent: RadioGroup.Skeleton,
            fieldProps: {
              numberOfOptions: 2,
              style: styles.specifications,
            },
          },
        ],
      },
      {
        label: t('Settings'),
        fields: [
          {
            key: 'holdtime',
            label: t('Hold time'),
          },
        ],
      },
    ],
  }
  return <Form.Skeleton style={style} config={config} numberOfbuttons={[2, 1]} {...otherProps} />
}
WorkspaceTypeForm.Skeleton = Skeleton

export default observer(WorkspaceTypeForm)
